
/**
 * Represents a contact with optional personal and contact information.
 */
class Contact {
    private readonly email: string | null;
    private readonly firstName: string | null;
    private readonly lastName: string | null;
    private readonly mobile: string | null;
    private readonly phone: string | null;

    /**
     * Constructs an instance of the class with optional user details.
     *
     * @param {string|null} email - The email address of the user. Defaults to null if not provided.
     * @param {string|null} firstName - The first name of the user. Defaults to null if not provided.
     * @param {string|null} lastName - The last name of the user. Defaults to null if not provided.
     * @param {string|null} mobile - The mobile number of the user. Defaults to null if not provided.
     * @param {string|null} phone - The phone number of the user. Defaults to null if not provided.
     */
    constructor(
        email: string | null = null,
        firstName: string | null = null,
        lastName: string | null = null,
        mobile: string | null = null,
        phone: string | null = null,
    ) {
        this.email = email;
        this.firstName = firstName;
        this.lastName = lastName;
        this.mobile = mobile;
        this.phone = phone;

    }

    /**
     * Converts a JSON object into a User instance.
     *
     * @param {any} item - The JSON object containing user data to be converted.
     * @return {Contact} A new User instance created from the provided JSON data.
     */
    public static fromJson(item: any): Contact {
        return new Contact(
            item.email,
            item.firstName,
            item.lastName,
            item.mobile,
            item.phone,
        );
    }

    /**
     * Get the email address.
     *
     * @return {string | null} The email address of the contact.
     */
    public getEmail(): string | null {
        return this.email;
    }

    /**
     * Get the first name.
     *
     * @return {string | null} The first name of the contact.
     */
    public getFirstName(): string | null {
        return this.firstName;
    }

    /**
     * Get the last name.
     *
     * @return {string | null} The last name of the contact.
     */
    public getLastName(): string | null {
        return this.lastName;
    }

    /**
     * Get the mobile phone number.
     *
     * @return {string | null} The mobile phone number of the contact.
     */
    public getMobile(): string | null {
        return this.mobile;
    }

    /**
     * Get the phone number.
     *
     * @return {string | null} The phone number of the contact.
     */
    public getPhone(): string | null {
        return this.phone;
    }

    /**
     * Retrieves the full name of the person by combining the first name and last name.
     *
     * @return {string | null} Returns the full name as a string if both first name and last name are present, otherwise returns null.
     */
    public getFullName(): string | null {
        return this.firstName + " " + this.lastName;
    }

}

export default Contact;